import {ApexOptions} from 'apexcharts'
import dayjs from 'dayjs'
import {flaggedReviewImageStatusOptions} from 'pages/customer/dataConfig/declearColumns'
import {useMemo} from 'react'
import ReactApexChart from 'react-apexcharts'

const toNum = (v: any): number | null => {
  if (v === null || v === undefined) return null
  const s = String(v).trim()
  if (s === '') return null
  const n = Number(s.replace(/,/g, ''))
  return Number.isFinite(n) ? n : null
}

const getShortDate = (value: any) => {
  const date = dayjs(value)
  return date.isValid() ? date.format('MM월 DD일') : '-'
}

const getTooltipDateTime = (value: any) => {
  const date = dayjs(value)
  return date.isValid() ? date.format('YYYY-MM-DD HH:mm:ss') : '-'
}

const getDateKey = (value: any) => {
  const date = dayjs(value)
  return date.isValid() ? date.format('YYYY-MM-DD') : '-'
}

const FlaggedReviewValueChart = ({
  rows,
  selectedOption,
  loading,
  chartHeight,
  onRowClick,
}: {
  rows: any[]
  selectedOption?: 'reading' | 'usage'
  loading?: boolean
  chartHeight?: number
  onRowClick?: (row: any) => void
}) => {
  const chartData = useMemo(() => {
    const sorted = [...(rows ?? [])].sort((a, b) =>
      String(a.idxDate).localeCompare(String(b.idxDate))
    )

    // 인식불가 등 검침값이 없는 행도 리뷰 상태 점을 찍을 수 있도록 직전 값을 이어서 사용
    // 수정된 행은 updateCheckValue를 우선 사용 (백엔드가 checkValue와 분리해서 내려줌)
    let lastKnownValue: number | null = null
    const withResolvedValue = sorted.map((row) => {
      const numeric = toNum(row.updateCheckValue ?? row.checkValue)
      const isMissingValue = numeric === null
      if (!isMissingValue) lastKnownValue = numeric
      return {...row, resolvedValue: isMissingValue ? lastKnownValue : numeric, isMissingValue}
    })

    const plottable = withResolvedValue.filter((row) => row.resolvedValue !== null)

    if (selectedOption === 'usage') {
      return plottable.map((row, index) => {
        const prev = plottable[index - 1]
        const usage = prev ? row.resolvedValue! - prev.resolvedValue! : 0
        return {...row, chartValue: usage}
      })
    }

    return plottable.map((row) => ({...row, chartValue: row.resolvedValue}))
  }, [rows, selectedOption])

  const getMarkerColor = (row: any) => {
    const issueTypes: string[] = row.issueTypes ?? []
    const matched = flaggedReviewImageStatusOptions.find((option) =>
      issueTypes.includes(option.key)
    )
    return matched?.chartColor
  }

  const discreteMarkers = chartData.reduce((acc: any[], row, index) => {
    const color = getMarkerColor(row)
    if (color) {
      acc.push({
        seriesIndex: 0,
        dataPointIndex: index,
        fillColor: color,
        strokeColor: '#fff',
        size: 6,
        shape: 'circle',
      })
    }
    return acc
  }, [])

  const categories = chartData.map((row, index) => {
    const currentDateKey = getDateKey(row.createAt)
    const prevDateKey = index > 0 ? getDateKey(chartData[index - 1].createAt) : ''
    return currentDateKey === prevDateKey ? '' : getShortDate(row.createAt)
  })

  const unit = selectedOption === 'usage' ? 't' : 'm³'

  const series = [
    {
      name: selectedOption === 'usage' ? '사용량' : '검침값(㎥)',
      data: chartData.map((row) => row.chartValue),
    },
  ]

  const options: ApexOptions = {
    chart: {
      type: 'area',
      height: chartHeight ?? 260,
      toolbar: {show: false},
      zoom: {enabled: false},
      fontFamily:
        'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      animations: {enabled: true, speed: 500},
      events: {
        dataPointSelection: (_event, _chartContext, config) => {
          const dataPointIndex = config?.dataPointIndex
          if (typeof dataPointIndex !== 'number' || dataPointIndex < 0) return
          const row = chartData[dataPointIndex]
          if (row) onRowClick?.(row)
        },
        markerClick: (_event, _chartContext, config) => {
          const dataPointIndex = config?.dataPointIndex
          if (typeof dataPointIndex !== 'number' || dataPointIndex < 0) return
          const row = chartData[dataPointIndex]
          if (row) onRowClick?.(row)
        },
      },
    },
    dataLabels: {enabled: false},
    stroke: {curve: 'smooth', width: 3},
    fill: {
      type: 'gradient',
      gradient: {shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.02, stops: [0, 90, 100]},
    },
    grid: {
      show: true,
      borderColor: '#f1f5f9',
      strokeDashArray: 0,
      padding: {top: 4, right: 12, bottom: 0, left: 4},
    },
    xaxis: {
      categories,
      labels: {
        show: true,
        rotate: 0,
        style: {colors: '#94a3b8', fontSize: '11px', fontWeight: 700},
      },
      axisBorder: {show: false},
      axisTicks: {show: false},
      tooltip: {enabled: false},
    },
    yaxis: {
      labels: {
        style: {colors: '#94a3b8', fontSize: '11px', fontWeight: 700},
        formatter: (value) => `${value.toLocaleString()}${unit}`,
      },
    },
    tooltip: {
      enabled: true,
      theme: 'light',
      // 날짜/검침값 글씨 크기를 키우고, dot 색을 해당 행의 이슈 상태 색으로 표시
      custom: ({series, seriesIndex, dataPointIndex}) => {
        const row = chartData[dataPointIndex]
        const dateStr = getTooltipDateTime(row?.createAt)
        const seriesName = selectedOption === 'usage' ? '사용량' : '검침값(㎥)'
        const rawValue = series?.[seriesIndex]?.[dataPointIndex]
        const valueStr = row?.isMissingValue
          ? '인식불가'
          : `${Number(rawValue ?? 0).toLocaleString()}${unit}`
        // 이슈 없는(정상) 점은 초록색으로 표시
        const dotColor = getMarkerColor(row) ?? '#22c55e'
        return (
          `<div style="padding:10px 14px; font-family:inherit;">` +
          `<div style="font-size:17px; color:#334155; margin-bottom:7px;">${dateStr}</div>` +
          `<div style="display:flex; align-items:center; gap:9px;">` +
          `<span style="width:13px; height:13px; border-radius:50%; background:${dotColor}; display:inline-block;"></span>` +
          `<span style="font-size:19px; font-weight:700; color:#0f172a;">${seriesName}: ${valueStr}</span>` +
          `</div>` +
          `</div>`
        )
      },
    },
    markers: {size: 0, strokeWidth: 3, hover: {size: 6}, discrete: discreteMarkers},
    colors: ['#2563eb'],
  }

  if (loading) {
    return (
      <div className='flex h-full items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-sm font-bold text-slate-400'>
        차트 데이터를 불러오는 중입니다.
      </div>
    )
  }

  if (chartData.length === 0) {
    return (
      <div className='flex h-full items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 text-sm font-bold text-slate-400'>
        선택한 기간에 차트 데이터가 없습니다.
      </div>
    )
  }

  return (
    <div className='h-full min-h-0 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm'>
      <ReactApexChart options={options} series={series} type='area' height='100%' />
    </div>
  )
}

export default FlaggedReviewValueChart
